"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, TrendingUp, Search, Users, Target, CircleCheck as CheckCircle2, ArrowRight, ChartBar as BarChart3, Building2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const marketStats = [{
  title: "Commercial Search Volume",
  value: "+42%",
  description: "Year-on-year increase in local commercial intent queries across Warrington and Cheshire North West hubs.",
  icon: TrendingUp
}, {
  title: "Mobile 'Near Me' Searches",
  value: "68%",
  description: "Local decisions triggered directly from mobile Google Map pack & high-intent organic listings.",
  icon: Search
}, {
  title: "B2B & Logistics Hubs",
  value: "Birchwood & Omega",
  description: "Major corporate and supply chain decision-makers actively researching local specialized vendors.",
  icon: Building2
}, {
  title: "Local Market Capture",
  value: "Top 3 Rank",
  description: "Captures over 75% of total high-value organic clicks in Warrington search engine results.",
  icon: Target
}];
const customerBehaviors = [{
  id: "discovery",
  title: "Initial Discovery",
  subtitle: "High-Intent Keyword Triggers",
  content: "Warrington consumers and B2B buyers start with specific location-modified searches. Whether searching for specialized consultancy near Birchwood Park or home services around Westbrook, they expect immediate authority and fast-loading localized answers.",
  bullets: ["Location-specific queries ('SEO consultant Warrington', 'B2B logistics Cheshire')", "High reliance on Google Business Profiles & Map Pack rankings", "Immediate comparison of local credentials & client outcomes"]
}, {
  id: "evaluation",
  title: "Trust Evaluation",
  subtitle: "Evaluating Direct Expertise",
  content: "Potential clients in Warrington look past generic agency claims. They want to verify who is actually handling their campaign, reviewing genuine case studies, technical capabilities, and direct consultant accessibility before reaching out.",
  bullets: ["Direct verification of consultant experience and local footprint", "In-depth review of actual ranking strategies and technical audits", "Preference for direct specialist contact over account managers"]
}, {
  id: "conversion",
  title: "Conversion Action",
  subtitle: "Frictionless Local Contact",
  content: "Warrington businesses demand clear, straightforward communication. When local searchers hit a optimized, high-converting landing page, clear call-to-actions convert research visits directly into booked strategy sessions.",
  bullets: ["Straightforward consultation booking without sales pressure", "Tailored campaign roadmaps built specifically for Cheshire business goals", "Transparent reporting with measurable local visibility metrics"]
}];
const localOpportunities = [{
  hub: "Birchwood Park & Enterprise Hubs",
  focus: "B2B, IT & Professional Services",
  insight: "High demand for specialized technical and B2B search visibility targeting regional corporate headquarters."
}, {
  hub: "Omega & Warrington South",
  focus: "Logistics, Supply Chain & E-commerce",
  insight: "Organic search opportunities for industrial suppliers, warehousing solutions, and national e-commerce distributors."
}, {
  hub: "Warrington Town Centre & Stockton Heath",
  focus: "Local Services, Legal & Healthcare",
  insight: "Competitive local Map Pack landscape where targeted schema, review velocity, and geo-targeted landing pages win."
}];
export default function WarringtonLandscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="warringtonmarketlandscape" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 px-3 py-1 mb-4 rounded-full text-xs sm:text-sm font-medium border border-accent/20">
            <MapPin className="size-3.5 text-accent mr-1 inline-block" />
            Cheshire & Warrington Local Search Insights
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-primary tracking-tight mb-4">
            Understanding Warrington&apos;s Local Search Landscape
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            As a strategically positioned commercial hub between Liverpool and Manchester, Warrington presents a thriving market with significant search opportunity. I help local businesses dominate high-value local intent queries.
          </p>
        </div>

        {/* Market Stats Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {marketStats.map((stat, i) => {
          const Icon = stat.icon;
          return <motion.div key={stat.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card hover:border-primary/30 transition-[border-color,box-shadow] duration-300 shadow-sm rounded-xl">
                  <CardHeader className="pb-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center mb-3">
                      <Icon className="size-5 text-primary" />
                    </div>
                    <span className="text-2xl sm:text-3xl font-bold text-primary font-serif">
                      {stat.value}
                    </span>
                    <CardTitle className="text-base font-semibold text-foreground mt-1">
                      {stat.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {stat.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Consumer Behaviour Breakdown */}
        <div className="bg-muted/40 rounded-2xl p-6 sm:p-10 border border-border mb-16">
          <div className="max-w-3xl mb-8">
            <span className="text-xs font-semibold text-accent uppercase tracking-wider block mb-2">
              Buyer Dynamics
            </span>
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-primary mb-3">
              How Warrington Customers Search & Convert
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              Local customer search behavior in Warrington combines regional commercial intent with high demand for trusted, hands-on specialists. Here is how we align your organic visibility with their buying journey.
            </p>
          </div>

          <Tabs defaultValue="discovery" className="w-full">
            <TabsList className="bg-background border border-border p-1 rounded-xl w-full sm:w-auto flex flex-col sm:flex-row h-auto gap-1 mb-6">
              <TabsTrigger value="discovery" className="w-full sm:w-auto px-5 py-2.5 rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm transition-colors">
                1. Search Discovery
              </TabsTrigger>
              <TabsTrigger value="evaluation" className="w-full sm:w-auto px-5 py-2.5 rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm transition-colors">
                2. Trust Evaluation
              </TabsTrigger>
              <TabsTrigger value="conversion" className="w-full sm:w-auto px-5 py-2.5 rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-medium text-sm transition-colors">
                3. Direct Conversion
              </TabsTrigger>
            </TabsList>

            {customerBehaviors.map((item, i) => <TabsContent key={item.id} value={item.id} className="mt-0 outline-none" data-index={i}>
                <Card className="border border-border bg-card shadow-xs rounded-xl p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    <div className="lg:col-span-7">
                      <Badge className="bg-secondary text-secondary-foreground mb-3 text-xs">
                        {item.subtitle}
                      </Badge>
                      <h4 className="text-xl font-serif font-semibold text-primary mb-3">
                        {item.title}
                      </h4>
                      <p className="text-foreground/90 text-sm sm:text-base mb-6 leading-relaxed">
                        {item.content}
                      </p>
                      <div className="space-y-2.5">
                        {item.bullets.map((bullet, idx) => <div key={idx} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                            <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </div>)}
                      </div>
                    </div>
                    <div className="lg:col-span-5 bg-muted/60 p-5 rounded-lg border border-border/60">
                      <h5 className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
                        Consultant Impact
                      </h5>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                        By working directly with me, your campaign is optimized specifically around these Warrington search characteristics—capturing intent before your regional competitors do.
                      </p>
                      <Separator className="my-3" />
                      <div className="flex items-center gap-2 text-xs font-medium text-primary">
                        <BarChart3 className="size-4 text-accent" />
                        <span>Tailored Warrington SEO Strategy</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Local Opportunity Districts */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-16">
          <div className="lg:col-span-5 space-y-4">
            <Badge variant="outline" className="border-accent/40 text-accent bg-accent/5 px-3 py-1">
              Geographic Focus
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-primary tracking-tight">
              Capturing High-Value Search Across Warrington Business Districts
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Warrington is not a one-size-fits-all market. Search intent varies drastically between the corporate B2B ecosystem in Birchwood, logistics operations around Omega, and local professional services in the Town Centre.
            </p>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed font-medium text-foreground">
              You work directly with me to build location-tailored landing pages, technical site structures, and authority signals that reflect actual local demand.
            </p>
            <div className="pt-2">
              <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg">
                <Link to="/LocalSEOPage">
                  Explore Local SEO Services
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-4">
            {localOpportunities.map((opp, i) => <div key={opp.hub} data-index={i} className="p-5 bg-card border border-border rounded-xl hover:border-primary/30 transition-[border-color,box-shadow] duration-300 shadow-xs">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-serif font-semibold text-base text-primary">
                    {opp.hub}
                  </h4>
                  <Badge variant="secondary" className="text-xs bg-secondary text-secondary-foreground">
                    {opp.focus}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {opp.insight}
                </p>
              </div>)}
          </div>
        </div>

        {/* Bottom CTA Banner */}
        <div className="bg-gradient-to-r from-primary to-primary/90 text-primary-foreground rounded-2xl p-8 sm:p-12 shadow-lg relative overflow-hidden">
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="max-w-2xl text-center md:text-left">
              <span className="inline-block px-3 py-1 bg-white/10 text-white rounded-full text-xs font-medium mb-3">
                Direct Consultant Access
              </span>
              <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-white tracking-tight mb-2">
                Ready to Grow Your Organic Visibility in Warrington?
              </h3>
              <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
                Book a direct consultation with me today. We will analyze your local competitors, unpack your current search performance, and map out a tailored strategy.
              </p>
            </div>
            <div className="shrink-0 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button asChild size="lg" variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold shadow-xs">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 dark:bg-transparent shadow-xs">
                <Link to="/CaseStudiesPage">
                  View Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}